import classNames from "classnames";
import { Card } from "~/Configurators/Linear/components/Card";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { SimpleCsticNumberField } from "~/Configurators/Shaft/components/SimpleCsticNumberField";
import classes from "../SegmentCommonStyles.module.less";
import { type CsticId, SimpleCsticId } from "~/Configurators/Linear/models";
import { INCH_UNIT, METRIC_UNIT } from "~/Configurators/Shaft/common/commonShaftConstants";
import { useMinMaxCsticLabel } from "~/Configurators/Shaft/components/useMinMaxCsticLabel";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { SimpleCsticTextField } from "~/Configurators/Shaft/components/SimpleCsticTextField";
import { radialThread } from "~/Configurators/Shaft/inch/inchCsticConstants";

interface Props {
  className?: string;
}

const AUTOMATIC_ALAR = "S";
const MANUAL_ALAR = "A";

const CONFIG = {
  pdt: {
    label: "configurator.shaft.step.2.substep.3.extraFields.radialThread.title",
    csticId: SimpleCsticId(radialThread.pdt),
  },
  threadSpacingInch: {
    label: "configurator.shaft.step.2.substep.3.extraFields.radialThreadSpacing.title.inch",
    csticId: SimpleCsticId(radialThread.threadSpacing.inch),
  },
  threadSpacingMm: {
    label: "configurator.shaft.step.2.substep.3.extraFields.radialThreadSpacing.title.metric",
    csticId: SimpleCsticId(radialThread.threadSpacing.metric),
  },
  al: {
    inch: {
      label: "configurator.shaft.step.2.substep.3.extraFields.al.title.inch",
      value: SimpleCsticId(radialThread.aL.inch.value),
      min: SimpleCsticId(radialThread.aL.inch.min),
      max: SimpleCsticId(radialThread.aL.inch.max),
      unit: INCH_UNIT,
      csticsToSet: [
        { key: radialThread.aL.metric.value, value: null },
        (val: string | null) => ({ key: radialThread.aLaREnabler, value: val === null ? AUTOMATIC_ALAR : MANUAL_ALAR }),
      ],
    },
    mm: {
      label: "configurator.shaft.step.2.substep.3.extraFields.al.title.metric",
      unit: METRIC_UNIT,
      value: SimpleCsticId(radialThread.aL.metric.value),
      min: SimpleCsticId(radialThread.aL.metric.min),
      max: SimpleCsticId(radialThread.aL.metric.max),
      disabled: true,
    },
  },
  ar: {
    inch: {
      label: "configurator.shaft.step.2.substep.3.extraFields.ar.title.inch",
      value: SimpleCsticId(radialThread.aR.inch.value),
      min: SimpleCsticId(radialThread.aR.inch.min),
      max: SimpleCsticId(radialThread.aR.inch.max),
      unit: INCH_UNIT,
      disabled: true,
    },
    mm: {
      label: "configurator.shaft.step.2.substep.3.extraFields.ar.title.metric",
      value: SimpleCsticId(radialThread.aR.metric.value),
      min: SimpleCsticId(radialThread.aR.metric.min),
      max: SimpleCsticId(radialThread.aR.metric.max),
      unit: METRIC_UNIT,
      disabled: true,
    },
  },
  threadHoleAmount: {
    label: "configurator.shaft.step.2.substep.3.extraFields.holeQuantity.title",
    csticId: SimpleCsticId(radialThread.threadHoleAmount),
  },
};

interface AlarInputProps {
  label: string;
  value: CsticId;
  min: CsticId;
  max: CsticId;
  unit: string;
  csticsToNull?: string[];
  csticsToSet?: Array<CsticToSet | Resolver>;
  disabled?: boolean;
  decimalScale?: number;
  substepType?: SubstepType;
  required?: boolean;
  error?: boolean;
}

type CsticToSet =
  | {
      key: string;
      value: string | null;
    }
  | undefined;

type Resolver = (value: string | null) => CsticToSet;

export const AlarInput = ({
  min,
  max,
  value,
  unit,
  decimalScale = 2,
  substepType = SubstepType.ShaftMachining,
  ...rest
}: AlarInputProps) => {
  const helperLabel = useMinMaxCsticLabel({ minCsticId: min, maxCsticId: max, unit });
  return (
    <SimpleCsticNumberField
      csticId={value}
      substepType={substepType}
      decimalScale={decimalScale}
      helperLabel={helperLabel}
      {...rest}
    />
  );
};

export const InchShaftRadialThreadSegment = (props: Props) => {
  const t = useXmlConfiguratorTranslations().t!;

  function translate<T extends { label: string }>(data: T) {
    return { ...data, label: t(data.label) };
  }
  return (
    <div className={classNames(classes.root, props.className)}>
      <Card className={classes.imageSection}>
        <img className={classes.image} src={t("configurator.shaft.step.2.substep.3.extraFields.center.image")} alt="" />
      </Card>
      <div className={classes.inputSection}>
        <SimpleCsticTextField {...translate(CONFIG.pdt)} substepType={SubstepType.ShaftMachining} disabled={true} />
        <SimpleCsticNumberField
          {...translate(CONFIG.threadSpacingInch)}
          substepType={SubstepType.ShaftMachining}
          disabled={true}
          decimalScale={2}
        />
        <SimpleCsticNumberField
          {...translate(CONFIG.threadSpacingMm)}
          substepType={SubstepType.ShaftMachining}
          disabled={true}
          decimalScale={2}
        />
        <div className={classes.alarSection}>
          <p className={classes.alarDescription}>{t("configurator.shaft.step.2.substep.3.extraFields.alar.title")}</p>
          <div className={classes.alarRow}>
            <AlarInput {...translate(CONFIG.al.inch)} decimalScale={4} />
            <AlarInput {...translate(CONFIG.ar.inch)} decimalScale={4} />
          </div>
          <div className={classes.alarRow}>
            <AlarInput {...translate(CONFIG.al.mm)} />
            <AlarInput {...translate(CONFIG.ar.mm)} />
          </div>
        </div>
        <SimpleCsticNumberField
          {...translate(CONFIG.threadHoleAmount)}
          substepType={SubstepType.ShaftMachining}
          disabled={true}
          decimalScale={0}
        />
      </div>
    </div>
  );
};
